<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import apiFetch from '@wordpress/api-fetch';
import draggable from 'vuedraggable';
import { useContactsStore } from './stores/contacts';
import { useCountriesStore } from './stores/countries';
import { useRegionsStore } from './stores/regions';
import { useSectorsStore } from './stores/sectors';
import { getUserEmail } from './utils/auth';
import { matomoTrackEvent } from './utils/matomo';
import SectorIcon from './assets/sector-icon.svg?component';
import CountryIcon from './assets/country-icon.svg?component';
import RegionIcon from './assets/region-icon.svg?component';
import OrganizationIcon from './assets/organization-icon.svg?component';
import NameIcon from './assets/name-icon.svg?component';
import RoleIcon from './assets/role-icon.svg?component';
import ArrowIcon from './assets/arrow.svg?component';
import SearchIcon from './assets/search-icon.svg?component';

const contactsStore = useContactsStore();
const countriesStore = useCountriesStore();
const regionsStore = useRegionsStore();
const sectorsStore = useSectorsStore();

const strings = ref(null);
const messageTextArea = ref(null);
const sort = ref({
  field: null,
  order: null,
});
const showFiltersMenu = ref(false);
const showSortMenu = ref(false);
const activeTaxonomy = ref(null);
const searchInput = ref('');
const searchQuery = ref('');
const isLoading = ref(false);

const lang = document.documentElement.lang?.substring(0, 2);

const headers = ref([
  { key: 'region', icon: RegionIcon },
  { key: 'country', icon: CountryIcon },
  { key: 'sector', icon: SectorIcon },
  { key: 'organization', icon: OrganizationIcon },
  { key: 'name', icon: NameIcon },
  { key: 'role', icon: RoleIcon }
]);

function formatCountriesAndRegions(items) {
  if (items.length === 0) {
    return '-';
  }

  if (items.length === 1) {
    return items[0];
  }

  const and = lang === 'pt' ? 'e' : lang === 'es' ? 'y' : 'and';

  if (items.length === 2) {
    return `${items[0]} ${and} ${items[1]}`;
  }

  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

function setSort(field, order) {
  sort.value.field = field;
  sort.value.order = order;
  matomoTrackEvent('contact-tool', `ct_sort_by_${field}_${order}`);
}

const sortedContacts = computed(() => {
  let filtered = contactsStore.contacts;

  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();

    filtered = filtered.filter(contact => {
      return (
        (contact.sector?.name || '').toLowerCase().includes(query) ||
        (contact.countries?.map(c => c.name).join(', ') || '').toLowerCase().includes(query) ||
        (contact.regions?.map(r => r.name).join(', ') || '').toLowerCase().includes(query) ||
        (contact.organization?.name || '').toLowerCase().includes(query) ||
        (contact.title || '').toLowerCase().includes(query) ||
        (contact.role?.name || '').toLowerCase().includes(query)
      );
    });
  }

  if (!sort.value.field) {
    return filtered;
  }

  return [...filtered].sort((a, b) => {
    const aVal = getContactValue(a, sort.value.field).toLowerCase();
    const bVal = getContactValue(b, sort.value.field).toLowerCase();

    return sort.value.order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
});

function getContactValue(contact, field) {
  switch(field) {
    case 'sector':
      return contact.sector?.name || '-';
    case 'country':
      const countries = formatCountriesAndRegions(contact.countries?.map(c => c.name) || []);
      if (countries == '-' && contact.regions?.length > 0) {
        return strings.value.all;
      }
      return countries;
    case 'region':
      return formatCountriesAndRegions(contact.regions?.map(r => r.name) || []);
    case 'organization':
      return contact.organization?.name || '-';
    case 'name':
      return contact.title || '-';
    case 'role':
      return contact.role?.name || '-';
    default:
      return '';
  }
}

const showContactModal = ref(false);
const activeContact = ref(null);
const userEmail = getUserEmail();
const contactForm = ref({
  message: '',
});
const contactMessage = ref('');
const contactLoading = ref(false);
const shouldCheckInput = ref(false);
const shouldShowMessageError = ref(false);

function openContactModal(contact) {
  matomoTrackEvent('contact-tool', 'ct_contact_click');
  activeContact.value = contact;
  contactForm.value = { message: '' };
  contactMessage.value = '';
  contactLoading.value = false;
  showContactModal.value = true;
}

function closeContactModal() {
  matomoTrackEvent('contact-tool', 'ct_cancel_sending_message');
  showContactModal.value = false;
}

function checkContactMessage()
{
  if (!shouldCheckInput.value) {
    return;
  }

  if (contactForm.value.message.length < 100 || contactForm.value.message.length > 500) {
    messageTextArea.value.classList.add('border-red!');
    messageTextArea.value.classList.add('text-red!');
    shouldShowMessageError.value = true;
  }
  else {
    messageTextArea.value.classList.remove('border-red!');
    messageTextArea.value.classList.remove('text-red!');
    shouldShowMessageError.value = false;
  }
}

async function submitContactMessage() {
  if (!activeContact.value) {
    return;
  }

  if (contactForm.value.message.length < 100 || contactForm.value.message.length > 500) {
    messageTextArea.value.classList.add('border-red!');
    messageTextArea.value.classList.add('text-red!');
    shouldCheckInput.value = true;
    shouldShowMessageError.value = true;
    return;
  }

  messageTextArea.value.classList.remove('border-red!');
  messageTextArea.value.classList.remove('text-red!');
  shouldCheckInput.value = false;
  shouldShowMessageError.value = false;

  matomoTrackEvent('contact-tool', 'ct_send_message');

  contactMessage.value = '';
  contactLoading.value = true;

  try {
    await apiFetch({
      path: '/wp-json/ebus/contact-message',
      method: 'POST',
      data: {
        contact_id: activeContact.value.id,
        message: contactForm.value.message,
        user_email: userEmail,
        lang,
      },
    });

    contactMessage.value = strings.value.contact_message_success;
    contactForm.value = { message: '' };
  } catch (error) {
    contactMessage.value = error.message;
  } finally {
    contactLoading.value = false;
  }
}

function toggleFiltersMenu() {
  showFiltersMenu.value = !showFiltersMenu.value;
  showSortMenu.value = false;
  activeTaxonomy.value = null;
  matomoTrackEvent('contact-tool', showFiltersMenu.value ? 'ct_open_filters' : 'ct_close_filters');
}

async function resetAllFilters() {
  countriesStore.resetActiveCountries();
  regionsStore.resetActiveRegions();
  sectorsStore.resetActiveSectors();
  searchInput.value = '';
  searchQuery.value = '';
  showFiltersMenu.value = false;
  showSortMenu.value = false;
  activeTaxonomy.value = null;
  isLoading.value = true;

  try {
    await contactsStore.loadContacts({ lang });
  } catch (error) {
    console.error('Error: ', error);
  } finally {
    isLoading.value = false;
  }
}

function toggleSectorTaxonomy() {
  activeTaxonomy.value = activeTaxonomy.value === 'sector' ? null : 'sector';

  if (activeTaxonomy.value === 'sector') {
    matomoTrackEvent('contact-tool', 'ct_sector_filter_open');
  }
}

function toggleCountryTaxonomy() {
  activeTaxonomy.value = activeTaxonomy.value === 'country' ? null : 'country';

  if (activeTaxonomy.value === 'country') {
    matomoTrackEvent('contact-tool', 'ct_country_filter_open');
  }
}

function toggleRegionTaxonomy() {
  activeTaxonomy.value = activeTaxonomy.value === 'region' ? null : 'region';

  if (activeTaxonomy.value === 'region') {
    matomoTrackEvent('contact-tool', 'ct_region_filter_open');
  }
}

function toggleSector(sector) {
  sectorsStore.toggleSector(sector.slug);
  matomoTrackEvent('contact-tool', 'ct_sector_filter_click', { sector: sector.name, status: sectorsStore.activeSectors.includes(sector.slug) });
}

function toggleCountry(country) {
  countriesStore.toggleCountry(country.code);
  matomoTrackEvent('contact-tool', 'ct_country_filter_click', { country: country.name, status: countriesStore.activeCountries.includes(country.code) });
}

function toggleRegion(region) {
  regionsStore.toggleRegion(region.slug);
  matomoTrackEvent('contact-tool', 'ct_region_filter_click', { region: region.name, status: regionsStore.activeRegions.includes(region.slug) });
}

function escapeCSV(value) {
  if (value === null || value === undefined) {
    return '';
  }
  
  const stringValue = String(value);
  
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
}

function downloadCSV() {
  matomoTrackEvent('contact-tool', 'ct_download_csv');

  const csvHeaders = headers.value.map(h => h.key.charAt(0).toUpperCase() + h.key.slice(1));
  
  const csvRows = sortedContacts.value.map(contact => {
    return headers.value.map(header => {
      const value = getContactValue(contact, header.key);
      return escapeCSV(value);
    }).join(',');
  });
  
  const csvContent = [
    csvHeaders.join(','),
    ...csvRows
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split('T')[0];
    
    link.setAttribute('href', url);
    link.setAttribute('download', `contacts_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

onMounted(async () => {
  try {
    const translations = await apiFetch({ path: `/wp-json/ebus/translations?lang=${lang}` });

    strings.value = translations;

    isLoading.value = true;

    await contactsStore.loadContacts({ lang });
  } catch (error) {
    console.error('Error: ', error);
  } finally {
    isLoading.value = false;
  }
});
countriesStore.loadCountries({ lang });
regionsStore.loadRegions({ lang });
sectorsStore.loadSectors({ lang });

watch(searchInput, (value) => {
  searchQuery.value = value;
});

watch(() => [countriesStore.activeCountries, regionsStore.activeRegions, sectorsStore.activeSectors], async () => {
  isLoading.value = true;

  try {
    await contactsStore.loadContacts({ lang, country: countriesStore.activeCountries, region: regionsStore.activeRegions, sector: sectorsStore.activeSectors });
  } catch (error) {
    console.error('Error: ', error);
  } finally {
    isLoading.value = false;
  }
}, { deep: true });
</script>

<template>  
  <section v-if="strings" class="w-full! min-h-screen! flex! flex-col! bg-light-aqua! px-6! pt-8! pb-25!">
    <h1 class="font-ibm-plex-mono! font-semibold! text-4xl! leading-tight! text-aqua! mb-6!">
      <RouterLink class="text-aqua!" to="/">{{ strings.ebus_contacts_database }}</RouterLink>
    </h1>

    <div class="w-full! flex! flex-col! gap-y-2.5! mb-12! lg:mb-5.5!">
      <div class="flex! flex-col! gap-y-3! lg:flex-row! lg:items-center! lg:justify-between!">
        <div class="flex! items-center! flex-wrap! gap-3!">
          <button 
            @click="toggleFiltersMenu"
            class="font-ibm-plex-mono! font-normal! text-lg! leading-tight! px-4! border! rounded-full! transition-colors!"
            :class="showFiltersMenu ? 'bg-black! border-black! text-white!' : 'border-light-green! text-light-green!'"
          >
            {{ strings.filters }} {{ !showFiltersMenu ? '+' : '-' }}
          </button>
          <button 
            @click="showSortMenu = !showSortMenu; showFiltersMenu = false; activeTaxonomy = null"
            class="lg:hidden! font-ibm-plex-mono! font-normal! text-lg! leading-tight! px-4! border! rounded-full! transition-colors!"
            :class="showSortMenu ? 'bg-black! border-black! text-white!' : 'border-light-green! text-light-green!'"
          >
            {{ strings.sort_by }} {{ !showSortMenu ? '+' : '-' }}
          </button>
          <button
            v-if="showFiltersMenu"
            @click="resetAllFilters"
            class="font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-black! px-3.5! border! border-black! rounded-full!"
          >
            {{ strings.reset_filters }}
          </button>
        </div>

        <div class="hidden lg:flex lg:max-w-68! items-end! gap-x-1!">
          <div class="font-ibm-plex-mono! font-medium! text-lg! leading-tight! text-black!">{{ strings.search }}</div>
          <div class="flex! items-end! gap-x-2!">
            <input
              v-model="searchInput"
              type="text"
              @focusout="searchInput.trim() && matomoTrackEvent('contact-tool', 'ct_search')"
              class="w-full! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! pb-0.5! border-b-2! border-black! placeholder:text-black!"
            />
            <SearchIcon class="[&>path]:fill-black!" />
          </div>
        </div>
      </div>

      <div v-if="showFiltersMenu" class="flex! gap-3! bg-gray/15! -mx-6! px-6! py-4! lg:mx-0! lg:px-0! lg:py-0! lg:bg-transparent!">
        <button 
          @click="toggleSectorTaxonomy"
          class="flex! items-center! gap-x-1! font-ibm-plex-mono! font-normal! text-base! lg:text-sm! leading-tight! text-black! px-3.5! py-0.5! border! rounded-full! transition-colors!"
          :class="activeTaxonomy === 'sector' ? 'font-medium! bg-aqua! border-aqua! text-white!' : 'font-normal! border-black!'"
        >
          <span>{{ strings.sector }}</span>
          <span v-if="sectorsStore.activeSectors.length > 0">
            ({{ sectorsStore.activeSectors.length }})
          </span>
        </button>
        <button 
          @click="toggleCountryTaxonomy"
          class="flex! items-center! gap-x-1! font-ibm-plex-mono! text-base! lg:text-sm! leading-tight! text-black! px-3.5! py-0.5! border! rounded-full! transition-colors!"
          :class="activeTaxonomy === 'country' ? 'font-medium! bg-aqua! border-aqua! text-white!' : 'font-normal! border-black!'"
        >
          <span>{{ strings.country }}</span>
          <span v-if="countriesStore.activeCountries.length > 0">
            ({{ countriesStore.activeCountries.length }})
          </span>
        </button>
        <button 
          @click="toggleRegionTaxonomy"
          class="flex! items-center! gap-x-1! font-ibm-plex-mono! text-base! lg:text-sm! leading-tight! text-black! px-3.5! py-0.5! border! rounded-full! transition-colors!"
          :class="activeTaxonomy === 'region' ? 'font-medium! bg-aqua! border-aqua! text-white!' : 'font-normal! border-black!'"
        >
          <span>{{ strings.region }}</span>
          <span v-if="regionsStore.activeRegions.length > 0">
            ({{ regionsStore.activeRegions.length }})
          </span>
        </button>
      </div>

      <div v-if="showFiltersMenu && activeTaxonomy === 'sector' && sectorsStore.sectors.length > 0" class="flex! flex-wrap! gap-x-4! gap-y-3.5! lg:gap-2! bg-gray/7.5! lg:bg-transparent! py-4! -mt-4! -mx-6! px-6! lg:mt-0! lg:mx-0! lg:px-0! lg:py-0!">
        <button 
          v-for="sector in sectorsStore.sectors"
          :key="sector"
          @click="toggleSector(sector)"
          class="flex! items-center! gap-x-1! font-ibm-plex-mono! text-sm! leading-tight! text-black! px-3.5! py-0.5! border! rounded-full! transition-colors!"
          :class="sectorsStore.activeSectors.includes(sector.slug) ? 'font-medium! bg-aqua! border-aqua! text-white!' : 'font-normal! border-black!'"
        >
          {{ sector.name }}
        </button>
      </div>

      <div v-if="showFiltersMenu && activeTaxonomy === 'country' && countriesStore.countries.length > 0" class="flex! flex-wrap! gap-x-4! gap-y-3.5! gap-2! bg-gray/7.5! lg:bg-transparent! py-4! -mt-4! -mx-6! px-6! lg:mt-0! lg:mx-0! lg:px-0! lg:py-0!">
        <button 
          v-for="country in countriesStore.countries"
          :key="country"
          @click="toggleCountry(country)"
          class="flex! items-center! gap-x-1! font-ibm-plex-mono! text-sm! leading-tight! text-black! px-3.5! py-0.5! border! rounded-full! transition-colors!"
          :class="countriesStore.activeCountries.includes(country.code) ? 'font-medium! bg-aqua! border-aqua! text-white!' : 'font-normal! border-black!'"
        >
          {{ country.name }}
        </button>
      </div>

      <div v-if="showFiltersMenu && activeTaxonomy === 'region' && regionsStore.regions.length > 0" class="flex! flex-wrap! gap-x-4! gap-y-3.5! gap-2! bg-gray/7.5! lg:bg-transparent! py-4! -mt-4! -mx-6! px-6! lg:mt-0! lg:mx-0! lg:px-0! lg:py-0!">
        <button 
          v-for="region in regionsStore.regions"
          :key="region"
          @click="toggleRegion(region)"
          class="flex! items-center! gap-x-1! font-ibm-plex-mono! text-sm! leading-tight! text-black! px-3.5! py-0.5! border! rounded-full! transition-colors!"
          :class="regionsStore.activeRegions.includes(region.slug) ? 'font-medium! bg-aqua! border-aqua! text-white!' : 'font-normal! border-black!'"
        >
          {{ region.name }}
        </button>
      </div>

      <div v-if="showSortMenu" class="lg:hidden! grid! grid-cols-2! gap-4! bg-gray/15! -mx-6! px-6! py-4!">
        <template v-for="header in headers" :key="header.key">
          <button
            @click="setSort(header.key, 'asc')"
            class="justify-self-start! flex! items-center! justify-between! gap-x-2! font-ibm-plex-mono! text-sm! leading-tight! px-4! py-0.5! border! rounded-full! transition-colors!"
            :class="sort.field === header.key && sort.order === 'asc' ? 'font-medium! bg-aqua! border-aqua! text-white!' : 'font-normal! border-black! text-black!'"
          >
            <span class="capitalize!">{{ strings[header.key] }}</span>
            <ArrowIcon 
              class="w-3! h-3! rotate-180!"
              :class="sort.field === header.key && sort.order === 'asc' ? '[&_path]:fill-white!' : '[&_path]:fill-black!'"
            />
          </button>
          <button
            @click="setSort(header.key, 'desc')"
            class="justify-self-start! flex! items-center! justify-between! gap-x-2! font-ibm-plex-mono! text-sm! leading-tight! px-4! py-0.5! border! rounded-full! transition-colors!"
            :class="sort.field === header.key && sort.order === 'desc' ? 'font-medium! bg-aqua! border-aqua! text-white!' : 'font-normal! border-black! text-black!'"
          >
            <span class="capitalize!">{{ strings[header.key] }}</span>
            <ArrowIcon 
              class="w-3! h-3! [&_path]:fill-black!"
              :class="sort.field === header.key && sort.order === 'desc' ? '[&_path]:fill-white!' : '[&_path]:fill-black!'"
            />
          </button>
        </template>
      </div>

      <div class="lg:hidden! w-full! max-w-68! flex! items-end! gap-x-2! mt-2!">
        <div class="font-ibm-plex-mono! font-medium! text-lg! leading-tight! text-black!">
          {{ strings.search }}
        </div>
        <div class="flex! items-end! gap-x-2! flex-1!">
          <input
            v-model="searchInput"
            type="text"
            @focusout="searchInput.trim() && matomoTrackEvent('contact-tool', 'ct_search')"
            class="w-full! font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! pb-0.5! border-b-2! border-black! placeholder:text-black!"
          />
          <SearchIcon class="[&>path]:fill-black!" />
        </div>
      </div>
    </div>

    <div class="lg:hidden! flex! flex-col!">
      <div
        v-for="(contact, index) in sortedContacts"
        :key="contact.id"
        :class="[index % 2 === 0 ? 'bg-gray/15!' : 'bg-gray/7.5!']"
        class="flex! flex-col! gap-y-1.5! p-3.5!"
      >
        <div
          v-for="header in headers"
          :key="header.key"
          class="flex! items-start! gap-x-3.5!"
        >
          <component
            v-if="header.icon"
            :is="header.icon"
            class="w-4! h-4! mt-0.5! [&>path]:fill-aqua!"
          />
          <div class="flex! flex-col! gap-y-0.75!">
            <span class="font-ibm-plex-mono! font-medium! text-base! leading-tight! text-black! capitalize!">
              {{ strings[header.key] }}
            </span>
            <span class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! wrap-break-word!">
              <a
                v-if="header.key === 'organization' && contact.organization?.link && contact.organization?.name"
                :href="contact.organization.link"
                target="_blank"
                class="text-black! underline!"
                @click.stop
              >
                {{ getContactValue(contact, header.key) }}
              </a>
              <span v-else>
                {{ getContactValue(contact, header.key) }}
              </span>
            </span>
          </div>
        </div>
        <div class="flex! items-start! gap-x-3.5!">
          <div class="flex! flex-col! gap-y-0.75!">
            <button
              class="self-start! font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-black! px-3! py-0.5! border! border-black! rounded-full! lowercase! transition-all hover:text-white! hover:bg-aqua!"
              @click="openContactModal(contact)"
            >
              {{ strings.contact }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="hidden lg:block overflow-x-auto!">
      <table class="w-full! border-collapse!">
        <thead>
          <draggable
            :model-value="headers"
            tag="tr"
            item-key="header => header.key"
            @update:model-value="headers = $event"
          >
            <template #item="{ element: header }">
              <th class="font-ibm-plex-mono! font-medium! text-base! xl:text-lg! 2xl:text-xl! leading-tight! text-black! text-left! capitalize! pb-2.5! pr-6! xl:pr-8! 2xl:pr-10!">
                <div class="flex! items-center! gap-x-2! xl:gap-x-2.25! 2xl:gap-x-2.5!">
                  <component 
                    v-if="header.icon"
                    :is="header.icon" 
                    class="w-4! h-4! 2xl:w-5! 2xl:h-5! [&>path]:fill-aqua!"
                  />
                  {{ strings[header.key] }}
                  <div class="flex! gap-1! ml-1!">
                    <button
                      class="flex! items-center! justify-center! py-0.75! xl:py-1! 2xl:py-1.5! px-1! xl:px-1.5! 2xl:px-2! border!"
                      :class="sort.field === header.key && sort.order === 'desc' ? 'bg-aqua! border-aqua! [&>svg]:fill-white!' : 'border-black! [&>svg]:fill-black!'"
                      @click="setSort(header.key, 'desc')"
                    >
                      <ArrowIcon class="w-1.2! h-2! xl:w-1.35! xl:h-2.25! 2xl:w-1.5! 2xl:h-2.5!" />
                    </button>
                    <button
                      class="flex! items-center! justify-center! py-0.75! xl:py-1! 2xl:py-1.5! px-1! xl:px-1.5! 2xl:px-2! border!"
                      :class="sort.field === header.key && sort.order === 'asc' ? 'bg-aqua! border-aqua! [&>svg]:fill-white!' : 'border-black! [&>svg]:fill-black!'"
                      @click="setSort(header.key, 'asc')"
                    >
                      <ArrowIcon class="w-1.2! h-2! xl:w-1.35! xl:h-2.25! 2xl:w-1.5! 2xl:h-2.5! rotate-180!" />
                    </button>
                  </div>
                </div>
              </th>
            </template>
          </draggable>
        </thead>
        <tbody>
          <tr
            v-for="(contact, index) in sortedContacts"
            :key="contact.id"
            class="group cursor-pointer transition-bg"
            @click="openContactModal(contact)"
            :class="[index % 2 === 0 ? 'bg-gray/15! hover:bg-gray/17.5!' : 'bg-gray/7.5! hover:bg-gray/10!']"
          >
            <td
              v-for="header in headers"
              :key="header.key"
              class="font-ibm-plex-mono! font-normal! text-sm! lg:text-xs! xl:text-sm! 2xl:text-base! leading-tight! text-black! pl-1! pr-3! xl:pl-7! xl:pr-5! 2xl:px-8! py-2.5!"
            >
              <a
                v-if="header.key === 'organization' && contact.organization?.link && contact.organization?.name"
                :href="contact.organization.link"
                target="_blank"
                class="text-black! underline!"
                @click.stop
              >
                {{ getContactValue(contact, header.key) }}
              </a>
              <span v-else>
                {{ getContactValue(contact, header.key) }}
              </span>
            </td>
            <td class="py-2! pr-5! xl:pr-6! 2xl:pr-7! text-left!">
              <div
                class="font-ibm-plex-mono! font-normal! text-sm! lg:text-xs! xl:text-sm! 2xl:text-base! leading-tight! text-black! px-2 xl:px-3! py-0.4! 2xl:px-3.5! 2xl:py-0.5! border! border-black! rounded-full! lowercase! transition-all group-hover:text-white! group-hover:bg-aqua!"                
              >
                {{ strings.contact }}
            </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="isLoading" class="px-6! py-12! text-center! flex! items-center! justify-center!">
      <span class="w-12! h-12! border-4! border-aqua! border-t-transparent! rounded-full! animate-spin!"></span>
    </div>

    <div v-else-if="sortedContacts.length === 0" class="px-6! py-12! text-center!">
      <p class="font-ibm-plex-mono! font-bold! text-lg! leading-tight! text-black!">
        {{ strings.no_contacts_found }}
      </p>
    </div>

    <button
      @click="downloadCSV"
      :disabled="sortedContacts.length === 0"
      class="lg:self-start! font-ibm-plex-mono! font-normal! text-lg! leading-tight! text-black! px-4! py-0.5! border! border-black! rounded-full! mt-12! lg:mt-9.5! transition-all hover:text-white! hover:bg-aqua! disabled:opacity-50 disabled:cursor-not-allowed!"
    >
      {{ strings.download_csv }}
    </button>

    <div v-if="showContactModal" class="fixed! inset-0! z-50! flex! items-center! justify-center! bg-gray/90! px-4!">
      <div class="relative! w-full! lg:max-w-142.5! flex! flex-col! gap-y-6! bg-light-aqua! p-6!">
        <div v-if="contactLoading" class="absolute! inset-0! bg-gray/90! flex! items-center! justify-center! z-10!">
          <span class="w-20! h-20! border-4! border-aqua! border-t-transparent! rounded-full! animate-spin!"></span>
        </div>
        <div class="flex! flex-col! gap-y-4!">
          <h2 class="font-ibm-plex-mono! font-medium! text-2xl! leading-tight! text-aqua!">
            {{ strings.contact_modal_title }} 
            <template v-if="activeContact.organization && activeContact.organization.name">{{ activeContact.organization.name }}</template>
            <template v-else>{{ activeContact.title }}</template>
          </h2>
          <p v-if="!contactMessage" class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black!">
            {{ strings.contact_modal_description }}
          </p>
        </div>

        <div v-if="contactMessage" class="font-ibm-plex-mono! font-normal! text-base! text-black!">
          {{ contactMessage }}
        </div>

        <div v-if="contactMessage" class="flex! items-center! gap-x-4!">          
          <button 
            class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! lowercase! px-4! py-0.5! border! border-black! rounded-full! transition-all hover:text-white! hover:bg-aqua!"
            @click="closeContactModal"
          >
            {{ strings.contact_modal_close }}
          </button>
        </div>

        <div v-if="!contactMessage" class="flex! flex-col! gap-y-4!">
          <div class="flex! flex-col! gap-y-2!">
            <textarea
              v-model="contactForm.message"
              class="w-full! font-ibm-plex-mono! font-normal! text-base! text-black! placeholder:text-black! border! border-black! p-3! resize-none! bg-transparent!"
              :placeholder="strings.contact_modal_message"
              rows="5"
              maxlength="500"
              minlength="100"
              required
              ref="messageTextArea"
              @input="checkContactMessage"
            />
            <div class="text-right! font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-black!">
              {{ contactForm.message.length }} / 500
            </div>
            <div v-if="shouldShowMessageError" class="font-ibm-plex-mono! font-normal! text-sm! leading-tight! text-red!">{{ strings.error_invalid_params }}</div>
          </div>
        </div>

        <div v-if="!contactMessage" class="flex! items-center! gap-x-4!">
          <button
            class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! lowercase! px-4! py-0.5! border! border-black! rounded-full! transition-all hover:text-white! hover:bg-aqua! disabled:opacity-60! disabled:cursor-not-allowed!"
            @click="submitContactMessage"
            :disabled="contactLoading"
          >
            {{ strings.contact_modal_send }}
          </button>
          <button 
            class="font-ibm-plex-mono! font-normal! text-base! leading-tight! text-black! lowercase! px-4! py-0.5! border! border-black! rounded-full! transition-all hover:text-white! hover:bg-aqua!"
            @click="closeContactModal"
          >
            {{ strings.contact_modal_cancel }}
          </button>
        </div>
      </div>
    </div>
  </section> 
</template>